
import React from 'react';
import {
  FaHotel,
  FaWifi,
  FaTv,
  FaConciergeBell,
  FaUtensils,
  FaUserTie,
} from "react-icons/fa";

const HotelWelcomeSection = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 py-8 px-4 sm:py-12 sm:px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-amber-900 mb-4">
            Welcome to <span className="text-orange-600">Hotel Prakash Palace</span>
          </h1>
          <div className="w-24 sm:w-32 h-1 sm:h-1.5 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto rounded-full"></div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
          {/* Left Column - Text Content */}
          <div className="space-y-6 sm:space-y-8">
            {/* Introduction Card */}
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 transform hover:scale-[1.02] transition-all duration-300 border-l-4 border-amber-500">
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
                Nestled in the heart of the sacred city of Varanasi, Hotel Prakash Palace offers a harmonious blend of traditional hospitality and modern comforts. Whether you're here for a spiritual journey, cultural exploration, or simply to unwind, our hotel provides the perfect base for your stay.
              </p>
            </div>

            {/* Location & Connectivity Card */}
            <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl shadow-xl p-6 sm:p-8 text-white">
              <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                Prime Location & Connectivity
              </h2>
              <p className="text-sm sm:text-base leading-relaxed">
                Located just minutes away from the iconic ghats of the Ganges and major landmarks like Kashi Vishwanath Temple and Sarnath. Well-connected to railway station, bus stand, and airport ensuring hassle-free travel.
              </p>
            </div>

            {/* Amenities Card */}
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
              <h2 className="text-xl sm:text-2xl font-bold text-amber-900 mb-4">
                Our Amenities
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {[
                  { icon: FaHotel, text: "Well-appointed Rooms" },
                  { icon: FaWifi, text: "Free Wi-Fi" },
                  { icon: FaTv, text: "Flat-screen TVs" },
                  { icon: FaConciergeBell, text: "24-Hour Room Service" },
                  { icon: FaUtensils, text: "Multi-cuisine Restaurant" },
                  { icon: FaUserTie, text: "Courteous Staff" },
                ].map((amenity, index) => {
                  const Icon = amenity.icon;
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 hover:bg-amber-50 rounded-lg transition-colors"
                    >
                      <Icon className="text-xl sm:text-2xl text-amber-700" />
                      <span className="text-sm sm:text-base text-gray-700">
                        {amenity.text}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Column - Visual & Additional Info */}
          <div className="space-y-6 sm:space-y-8">
            {/* Experience Card */}
            <div className="relative rounded-2xl shadow-xl overflow-hidden h-64 sm:h-80 md:h-96 bg-gradient-to-br from-amber-900 to-orange-800">
              <div className="absolute inset-0 bg-opacity-60 p-6 sm:p-8 text-white flex flex-col justify-end">
                <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Experience Varanasi</h2>
                <p className="text-base sm:text-lg leading-relaxed">
                  Watch mesmerizing Ganga Aarti, explore narrow streets of the old city, enjoy peaceful boat rides on the Ganges. We make your visit truly special.
                </p>
              </div>
            </div>

            {/* Restaurant Card */}
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border-t-4 border-orange-500">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="bg-orange-100 p-3 sm:p-4 rounded-xl">
                  <span className="text-2xl sm:text-3xl">🍽️</span>
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-amber-900 mb-2">Culinary Delights</h3>
                  <p className="text-sm sm:text-base text-gray-700">
                    Indulge in our in-house restaurant serving authentic Indian dishes and international favorites, crafted to delight your taste buds.
                  </p>
                </div>
              </div>
            </div>

            {/* Final Call-to-Action */}
            <div className="text-center bg-gradient-to-r from-amber-100 to-orange-100 rounded-2xl p-6 sm:p-8 shadow-lg">
              <h3 className="text-2xl sm:text-3xl font-serif font-bold text-amber-900 mb-3 sm:mb-4">
                Your Home Away From Home
              </h3>
              <p className="text-gray-700 text-base sm:text-lg mb-4 sm:mb-6">
                Come, discover the charm of Varanasi with us at Hotel Prakash Palace
              </p>
              <button className="bg-gradient-to-r from-amber-600 to-orange-600 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-full font-semibold text-base sm:text-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300">
                Book Your Stay Now
              </button>
            </div>
          </div>
        </div>

        {/* Decorative Bottom Border */}
        <div className="mt-8 sm:mt-12 flex justify-center space-x-3 sm:space-x-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-gradient-to-r from-amber-400 to-orange-400"></div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HotelWelcomeSection;